import React, { useEffect, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  makeStyles,
  IconButton,
} from '@material-ui/core'
import { Delete as DeleteIcon } from '@material-ui/icons'
import { Method } from 'axios'

import { useApiCall } from 'hooks'
import { snackbar } from 'components'
import { FONTS } from 'styles'

import { InvoiceApi } from './api'
import { InvoiceFormData, InvoicePayload } from './types'

interface UserData {
  fullname: string
  email: string
  id: number
}

interface ApiResponse {
  user: UserData
}

interface ApiError {
  response?: {
    data?: {
      error?: {
        message: string
        type: string
      }
    }
    status?: number
  }
}

const useStyles = makeStyles(theme => ({
  dialogContent: {
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
  },
  form: {
    width: '100%',
    maxWidth: '500px',
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  photoPreview: {
    width: '100px',
    height: '100px',
    objectFit: 'cover',
    marginTop: theme.spacing(1),
  },
  userInfo: {
    textAlign: 'left',
    marginBottom: theme.spacing(3),
  },
  button: {
    fontFamily: FONTS.GOTHAM_LIGHT,
    fontSize: '18px',
    backgroundColor: theme.palette.primary.main,
    textTransform: 'capitalize',
    height: '45px',
    borderRadius: '10px',
    width: 200,
    color: theme.palette.background.default,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}))

const isValidQrHash = (hash: string): boolean => {
  return /^[A-Z0-9]{10}$/.test(hash)
}

const getUserDataRequest = (qrHash: string) => ({
  method: 'GET' as Method,
  url: `/users/hash/${qrHash}`,
})

export const InvoiceForm: React.FC = () => {
  const classes = useStyles()
  const { userId } = useParams<{ userId: string }>()
  const history = useHistory()
  const [userData, setUserData] = useState<UserData | null>(null)
  const [formData, setFormData] = useState<InvoiceFormData>({
    rut: '',
    serie: '',
    serieNumber: '',
    currency: 'UYU',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    photo: null,
  })
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)

  const [getUserData] = useApiCall<string, ApiResponse>(getUserDataRequest)
  const [createInvoice] = useApiCall<InvoicePayload, any>(InvoiceApi.create)

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (!userId) {
          throw new Error('Hash del QR requerido')
        }
        if (!isValidQrHash(userId)) {
          throw new Error(
            'Hash del QR inválido. Debe contener solo letras mayúsculas y números, y tener exactamente 10 caracteres.',
          )
        }
        const response = await getUserData(userId)
        if (response && response.user) {
          setUserData(response.user)
        } else {
          throw new Error('Datos de usuario no encontrados')
        }
      } catch (error) {
        console.error('Error fetching user data:', error)
        snackbar.show('Error al cargar los datos del usuario. Por favor, intente nuevamente.')
        history.push('/invoice/menu')
      }
    }
    fetchUserData()
  }, [userId, getUserData, history])

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>,
  ) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name as string]: value,
    }))
  }

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setFormData(prev => ({ ...prev, photo: file }))
      const reader = new FileReader()
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemovePhoto = () => {
    setFormData(prev => ({ ...prev, photo: null }))
    setPhotoPreview(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      let adminId
      const adminStorage = localStorage.getItem('admin')

      if (adminStorage) {
        const admin = JSON.parse(adminStorage) as { id: number }
        adminId = admin.id
      }

      await createInvoice({
        ...formData,
        userId: userData?.id,
        amount: Math.round(parseFloat(formData.amount.replace(',', '.')) * 100),
        adminId,
      })
      snackbar.show('Factura creada exitosamente')
      history.push('/invoice/menu')
    } catch (error) {
      if (error?.type === 'InvoiceAlreadyScannedError') {
        if (error?.params?.user) {
          const user = error?.params?.user
          snackbar.show(
            `Esta factura ya fue escaneada por el usuario: ${user.fullname} - ${user.email}`,
          )
        } else {
          snackbar.show('Esta factura ya fue escaneada')
        }
      } else {
        snackbar.show('Error al crear la factura. Por favor, intente nuevamente.')
      }
    }
  }

  const handleClose = () => {
    history.push('/invoice/menu')
  }

  if (!userData) {
    return null
  }

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Nueva Factura</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <form className={classes.form} onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <div className={classes.userInfo}>
                <Typography variant="h6" gutterBottom>
                  Datos del Usuario
                </Typography>
                <Typography>
                  Nombre completo: <strong>{userData.fullname}</strong>
                </Typography>
                <Typography>
                  Email: <strong>{userData.email}</strong>
                </Typography>
              </div>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="RUT"
                name="rut"
                value={formData.rut}
                onChange={handleInputChange}
                inputProps={{ maxLength: 12, pattern: '[0-9]*' }}
                required
                className={classes.field}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Serie"
                name="serie"
                value={formData.serie}
                onChange={handleInputChange}
                inputProps={{ maxLength: 5 }}
                required
                className={classes.field}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Número de serie"
                name="serieNumber"
                value={formData.serieNumber}
                onChange={handleInputChange}
                inputProps={{ pattern: '[0-9]*' }}
                required
                className={classes.field}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth className={classes.field}>
                <InputLabel>Moneda</InputLabel>
                <Select
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                  required
                >
                  <MenuItem value="UYU">UYU</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Importe"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                inputProps={{ pattern: '^\\d{1,5},\\d{2}$' }}
                placeholder="15000,00"
                required
                className={classes.field}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Fecha"
                name="date"
                type="date"
                value={formData.date}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                required
                className={classes.field}
              />
            </Grid>

            <Grid item xs={12}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="photo-upload"
                type="file"
                onChange={handlePhotoChange}
              />
              <label htmlFor="photo-upload">
                <Button variant="contained" component="span">
                  Subir foto
                </Button>
              </label>
              {photoPreview && (
                <div style={{ position: 'relative', display: 'inline-block', marginLeft: '16px' }}>
                  <img src={photoPreview} alt="Preview" className={classes.photoPreview} />
                  <IconButton
                    size="small"
                    onClick={handleRemovePhoto}
                    style={{ position: 'absolute', top: 0, right: 0 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>
              )}
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions style={{ justifyContent: 'center', padding: '16px' }}>
        <Button onClick={handleClose} variant="outlined" style={{ marginRight: '16px' }}>
          Cancelar
        </Button>
        <Button onClick={handleSubmit} className={classes.button}>
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  )
}
